import { useCallback, useEffect, useMemo, useRef } from "react";
import { get, isNil } from "lodash";
import PropTypes from "prop-types";
import ScreenRenderer from "shared/components/ScreenRenderer";
import { useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { previewVersion, snapshotHtml } from "./workshop-preview";

const SETTLE_DELAY = 300;

const WorkshopSampleRenderer = ({
  projectId,
  processId,
  htmlContent,
  styleContent,
  slots,
  data,
  refreshKey,
  onRendered,
}) => {
  const { data: projectData, refetch } = useQuery(graphql.queries.PROJECT, {
    variables: { where: { id: projectId } },
    fetchPolicy: "network-only",
    skip: isNil(projectId),
  });
  const project = get(projectData, "project") || null;
  const version = useMemo(
    () => previewVersion({ htmlContent, styleContent, slots }),
    [htmlContent, styleContent, slots],
  );

  const docRef = useRef(null);
  const observerRef = useRef(null);
  const timerRef = useRef(null);
  const onRenderedRef = useRef(onRendered);
  onRenderedRef.current = onRendered;

  const report = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const doc = docRef.current;
      if (isNil(doc) || isNil(doc.body)) return;
      onRenderedRef.current({ html: snapshotHtml(doc) });
    }, SETTLE_DELAY);
  }, []);

  const watch = useCallback(
    (doc) => {
      if (observerRef.current) observerRef.current.disconnect();
      docRef.current = doc;
      const observer = new MutationObserver(report);
      observer.observe(doc, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
      observerRef.current = observer;
      report();
    },
    [report],
  );

  useEffect(() => {
    if (isNil(projectId) || !refreshKey) return;
    refetch();
  }, [projectId, refreshKey, refetch]);

  useEffect(() => {
    report();
  }, [project, slots, report]);

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (observerRef.current) observerRef.current.disconnect();
    },
    [],
  );

  if (isNil(project)) return null;

  return (
    <div className="workshop-sample-renderer" aria-hidden="true">
      <ScreenRenderer
        preview
        version={version}
        project={project}
        data={data || {}}
        processId={processId}
        onDocument={watch}
      />
    </div>
  );
};

WorkshopSampleRenderer.propTypes = {
  projectId: PropTypes.string,
  processId: PropTypes.string,
  htmlContent: PropTypes.string,
  styleContent: PropTypes.string,
  slots: PropTypes.array,
  data: PropTypes.object,
  refreshKey: PropTypes.number,
  onRendered: PropTypes.func.isRequired,
};

WorkshopSampleRenderer.defaultProps = {
  projectId: null,
  processId: null,
  htmlContent: "",
  styleContent: "",
  slots: [],
  data: null,
  refreshKey: 0,
};

export default WorkshopSampleRenderer;
